
import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import {
  USER_NAMESPACE_PATH,
  HISTORY_NAMESPACE_PATH,
} from '@/store/namespaces.type';
import { historyItem } from '@/store/types';
import {
  IS_ADMIN,
  IS_ADMIN_OR_SUPPORT,
  SETTINGS_NUMBER_OF_ITEMS_IN_HISTORY,
  CAMPUS_VISITED_HISTORY,
} from '@/store/getters.type';
import { ADD_CAMPUS_TO_VISITED_HISTORY } from '@/store/actions.type';
import { REMOVE_CAMPUS_VISITED } from '@/store/mutations.type';
import { CampusWithUniqueLocations, DataOptions } from '@/api/interfaces';
import { repositories } from '@/api/ApiFactory';
import { Representations } from '@/components/shared/representation';
import VisitedHistoryBar from '@/views/campus/VisitedHistoryBar.vue';
import ErrorHandler from '@/components/shared/errorHandler';
import debounce from 'lodash.debounce';
import Breadcrumbs from '@/views/Breadcrumbs.vue';
import { getCampusesWithUniqueLocations } from '@/helpers';

const userModule = namespace(USER_NAMESPACE_PATH);
const historyModule = namespace(HISTORY_NAMESPACE_PATH);

@Component({
  components: {
    breadcrumbs: Breadcrumbs,
    visitedHistoryBar: VisitedHistoryBar,
  },
})
export default class CampusList extends Vue {
  @userModule.Getter(IS_ADMIN) public isAdmin!: boolean;
  @userModule.Getter(IS_ADMIN_OR_SUPPORT) public isAdminOrSupport!: boolean;
  @userModule.Getter(SETTINGS_NUMBER_OF_ITEMS_IN_HISTORY)
  public numberOfItemsInHistory!: boolean;

  @historyModule.Getter(CAMPUS_VISITED_HISTORY)
  public campusVisitedHistory!: historyItem[];
  @historyModule.Action(ADD_CAMPUS_TO_VISITED_HISTORY)
  public addCampusToVisitedHistory!: (item: historyItem) => Promise<any>;
  @historyModule.Mutation(REMOVE_CAMPUS_VISITED)
  public removeItemFromVisitedHistory!: (key: string) => void;

  private repr = new Representations();
  private indeterminate = true;
  private rowsPerPageItems = [50, 200, 500, 2000];
  private headers = [
    {
      text: 'Oznaka',
      align: 'left',
      sortable: true,
      value: 'slug',
      width: '15%',
    },
    { text: 'Ime', value: 'name', width: '25%', sortable: true },
    {
      text: 'Organizacije',
      value: 'organizations',
      width: '30%',
      sortable: false,
    },
    { text: 'Lokacije', value: 'locations', width: '30%', sortable: false },
  ];
  private search = '';
  private debouncedUpdateDebouncedSearch = debounce(
    this.updateDebouncedSearch,
    300,
  );
  private campuses: CampusWithUniqueLocations[] = [];
  private totalCampuses = 0;
  private loading = true;
  private options: DataOptions | null = null;

  private routeToCampus(item: CampusWithUniqueLocations | historyItem) {
    const campusSlug =
      'value' in item ? (item as historyItem).value : item.slug;
    this.$router.push({
      name: 'organization',
      params: { campusSlug: campusSlug },
    });
  }

  private routeToCampusAdd() {
    this.$router.push({ name: 'campusesAdd' });
  }

  @Watch('options', { deep: true })
  private handleOptions() {
    this.fetchCampuses(this.options);
  }

  private async fetchCampuses(pagination: DataOptions | null) {
    this.loading = true;
    try {
      const { data } = await repositories.tenants.campus.getCampuses(
        pagination,
        this.search,
        [],
      );
      const { campuses, errors } = getCampusesWithUniqueLocations(data.results);
      for (const error of errors) {
        this.$toasted.error(error);
      }
      this.campuses = campuses;
      this.totalCampuses = data.count;
    } catch (error) {
      this.$toasted.error(new ErrorHandler({ error, status: true }).toString());
    }
    this.loading = false;
  }

  @Watch('search')
  /**
   * @description debounce of search is implemented with 2 variables, where
   * the debouncedSearch is debounced for X time from the search change
   */
  private onSearchChange() {
    this.debouncedUpdateDebouncedSearch();
  }
  private updateDebouncedSearch() {
    // after a new search you want to be looking at the first page
    // manually change pagination object so that @update.pagination on data table gets triggered,
    // otherwise it doesn't update number of pages/items
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.options!.page = 1;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.fetchCampuses(this.options!);
  }
}
